/**
 * O cartão "Próximo passo": a única ação primária da tela (spec #387, seção 5.3).
 *
 * Três situações, todas com texto: há passo que a pessoa consegue dar (o único botão
 * `contained`), há pendência fora do alcance dela (o cartão diz a quem pedir, sem botão), ou
 * tudo está concluído. O progresso fica ao lado, em número e em barra.
 */

import React from 'react';
import { Box, Card, CardContent, Chip, LinearProgress, Typography } from '@mui/material';
import { CheckCircle as CheckIcon, ChevronRight as GoIcon } from '@mui/icons-material';

import { RouteActionButton } from '../../../components';
import type { Perspectiva } from '../../../types/manifesto';
import type { ProximoPasso } from '../proximoPasso';
import { ESTADOS_DA_TELA, FRASES, QUEM_RESOLVE, TEXTO_DO_ESTADO } from '../textos';

export interface NextStepCardProps {
  passo: ProximoPasso | null;
  perspectiva: Perspectiva;
  progresso: { feitos: number; total: number };
  concluido: boolean;
}

/** Quantas empresas faltam, só quando o item é medido em várias. */
function quantasFaltam(passo: ProximoPasso): number | undefined {
  const { item } = passo;
  if (item.total === undefined || item.total <= 1) return undefined;
  return item.missingTotal ?? (item.ready !== undefined ? item.total - item.ready : undefined);
}

export const NextStepCard: React.FC<NextStepCardProps> = ({
  passo,
  perspectiva,
  progresso,
  concluido,
}) => {
  const percentual = progresso.total > 0 ? Math.round((progresso.feitos / progresso.total) * 100) : 0;

  if (concluido) {
    return (
      <Card variant="outlined" sx={{ borderLeft: 4, borderLeftColor: 'success.main' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography component="h2" variant="h6" fontWeight={700}>
            {ESTADOS_DA_TELA.concluido.titulo}
          </Typography>
          <Typography sx={{ fontSize: '16px', mt: 1, mb: 2 }}>{ESTADOS_DA_TELA.concluido.texto}</Typography>
          <Chip color="success" variant="outlined" icon={<CheckIcon />} label={TEXTO_DO_ESTADO.done} />
        </CardContent>
      </Card>
    );
  }

  const titulo = passo
    ? passo.texto.proximo?.titulo(quantasFaltam(passo)) ?? passo.acao.rotulo
    : FRASES.semAcao;
  const texto = passo
    ? passo.texto.proximo?.texto
    : `Peça a ${QUEM_RESOLVE[perspectiva]} para fazer o que está pendente.`;

  return (
    <Card variant="outlined" component="section" aria-labelledby="proximo-passo-titulo">
      <CardContent sx={{ p: 3 }}>
        <Typography
          sx={{ fontSize: '14px', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}
        >
          {FRASES.proximoPasso}
        </Typography>
        <Typography id="proximo-passo-titulo" component="h2" variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
          {titulo}
        </Typography>
        {texto && <Typography sx={{ fontSize: '16px', mt: 1 }}>{texto}</Typography>}

        {passo && (
          <RouteActionButton
            para={passo.acao.rota}
            exige={passo.acao.exige}
            variant="contained"
            size="large"
            endIcon={<GoIcon />}
            sx={{ mt: 2, minHeight: 48, fontSize: '16px' }}
          >
            {passo.acao.rotulo}
          </RouteActionButton>
        )}

        {progresso.total > 0 && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress
              variant="determinate"
              value={percentual}
              aria-label={`${progresso.feitos} de ${progresso.total} itens concluídos`}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography sx={{ fontSize: '16px', mt: 1 }}>
              <b>
                {progresso.feitos} de {progresso.total}
              </b>{' '}
              itens concluídos
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NextStepCard;
